import { gql } from '@apollo/client'
import { graphql } from '@/graphql-setup'
import { ThumbnailUserFragment } from '@/modules/users/fragments'

export const IssueFragments = {
  issueCategoryInfo: gql`
    fragment IssueCategoryInfo on IssueCategoryNode {
      id
      name
      color
      responsibleUser {
        id
        fullName
        initials
        profilePicture
      }
      caseHandler {
        id
        fullName
        initials
        profilePicture
      }
      notificationConfig
      defaultSeverity
    }
  `,
  issueAttachmentInfo: gql`
    fragment IssueAttachmentInfo on IssueAttachmentNode {
      id
      name
      file
    }
  `,
  issueCategory: gql`
    fragment IssueCategory on IssueNode {
      category {
        id
        name
        color
        responsibleUser {
          id
          fullName
          initials
          profilePicture
        }
        caseHandler {
          id
          fullName
          initials
          profilePicture
        }
        notificationConfig
      }
    }
  `,
}

export const ISSUE_QUERY = gql`
  query Issue($id: ID!) {
    issue(id: $id) {
      id

      referenceId
      vehiclePlateNumber
      title
      issueDescription
      reason
      damageDescription
      immediateAction
      action

      costEstimation
      actualCost
      solutionSuggestion
      solution

      locationPlacename
      locationLatitude
      locationLongitude
      locationDescription

      relatedLink
      subcategory {
        id
        name
        defaultSeverity
      }
      category {
        id
        name
        color
        defaultExportText

        responsibleUser {
          ...ThumbnailUser
        }
        caseHandler {
          ...ThumbnailUser
        }
      }
      riskAspect {
        id
        name
      }
      processChoice {
        id
        name
        order
        process {
          id
          name
        }
      }

      relationOptions {
        edges {
          node {
            id
            name
            relation {
              id
              name
            }
          }
        }
      }

      tags {
        edges {
          node {
            id
            name
            color
            description
          }
        }
      }

      timeOfIssueStart
      timeOfIssueEnd
      user {
        ...ThumbnailUser
      }
      archivedAt
      archivedBy {
        ...ThumbnailUser
      }
      closedAt
      closedBy {
        ...ThumbnailUser
      }

      attachments {
        id
        name
        file
      }
      responsibleUser {
        ...ThumbnailUser
      }
      caseHandler {
        ...ThumbnailUser
      }
      severity
      actionLogs {
        id
        action
        metadata
        createdAt
        user {
          id
          fullName
        }
        secondaryUser {
          id
          fullName
        }
      }
    }
  }
  ${ThumbnailUserFragment}
`

export const ALL_ISSUES_QUERY = graphql(`
  query AllIssues(
    $first: Int
    $after: String
    $company: ID!
    $q: String
    $orderBy: String
    $archivedAtIsNull: Boolean
    $includeStatuses: [IssueStatus]
    $excludeCategories: [ID]
    $includeSeverities: [String]
    $relatedInfringement: String
    $onlyManuallyCreated: Boolean
    $isInvolved: Boolean
    $leaderGroupId: ID
    $endDate: DateTime
    $startDate: DateTime
    $createdStart: DateTime
    $createdEnd: DateTime
    $tags: [ID]
  ) {
    allIssues(
      first: $first
      after: $after
      company: $company
      q: $q
      orderBy: $orderBy
      archivedAt_Isnull: $archivedAtIsNull
      includeStatuses: $includeStatuses
      excludeCategories: $excludeCategories
      includeSeverities: $includeSeverities
      relatedInfringement: $relatedInfringement
      onlyManuallyCreated: $onlyManuallyCreated
      isInvolved: $isInvolved
      leaderGroupId: $leaderGroupId
      datetimeStrictStart: $startDate
      datetimeStrictEnd: $endDate
      createdStrictStart: $createdStart
      createdStrictEnd: $createdEnd
      tags: $tags
    ) {
      total
      pageInfo {
        endCursor
        hasNextPage
      }
      edges {
        node {
          id

          timeOfIssueStart
          timeOfIssueEnd
          closed
          archived
          edited

          vehiclePlateNumber
          referenceId
          title
          issueDescription

          solution
          actualCost

          riskAspect {
            id
            name
          }
          severity

          category {
            id
            name
            color
          }
          user {
            id
            fullName
            phoneNumber
          }
          responsibleUser {
            id
            fullName
          }
          caseHandler {
            id
            fullName
          }
          subcategory {
            id
            name
          }
          processChoice {
            id
            name
          }
          tags {
            edges {
              node {
                id
                name
                color
              }
            }
          }
          locationPlacename
          createdAt
          costEstimation
          closedAt
          selectedRelationOptions {
            id
            name
            relation {
              id
            }
          }
        }
      }
    }
  }
`)

export const ALL_ISSUE_CATEGORIES_QUERY = gql`
  query AllIssueCategories {
    allIssueCategories {
      edges {
        node {
          ...IssueCategoryInfo
        }
      }
    }
  }
  ${IssueFragments.issueCategoryInfo}
`

export const ALL_ISSUE_RISK_ASPECTS_QUERY = gql`
  query AllIssueRiskAspects {
    allIssueRiskAspects {
      edges {
        node {
          id
          name
        }
      }
    }
  }
`

export const ALL_ISSUE_RELATIONS_QUERY = gql`
  query AllIssueRelations {
    allIssueRelations {
      edges {
        node {
          id
          name
          multipleChoice
          visibleOnCreate
          requiredOnCreate
          visibleOnEdit
          requiredOnEdit
          visibleInTable
          options {
            edges {
              node {
                id
                name
              }
            }
          }
        }
      }
    }
  }
`
export const ALL_ISSUE_PROCESSES_QUERY = gql`
  query AllIssueProcesses {
    allIssueProcesses {
      edges {
        node {
          id
          name
          choices {
            edges {
              node {
                id
                name
                order
              }
            }
          }
        }
      }
    }
  }
`
export const ALL_ISSUE_CATEGORIES_SHALLOW_QUERY = gql`
  query AllIssueCategoriesShallow($withResponsibleUser: Boolean = false) {
    allIssueCategories {
      edges {
        node {
          id
          name
          color
          defaultSeverity
          responsibleUser @include(if: $withResponsibleUser) {
            id
            fullName
          }
          caseHandler @include(if: $withResponsibleUser) {
            id
            fullName
          }
        }
      }
    }
  }
`
export const DELETE_SUBCATEGORY = gql`
  mutation DeleteIssueSubcategory($id: ID!) {
    deleteIssueSubcategory(id: $id) {
      deletedId
    }
  }
`
export const PATCH_SUBCATEGORY = gql`
  mutation PatchIssueSubcategory(
    $id: ID!
    $input: PatchIssueSubcategoryInput!
  ) {
    patchIssueSubcategory(id: $id, input: $input) {
      issueSubcategory {
        id
        name
        defaultSeverity
        responsibleUser {
          id
        }
        caseHandler {
          id
        }
      }
    }
  }
`

export const GET_ALL_ISSUE_SUBCATEGORIES = graphql(`
  query AllIssueSubcategories($categoryId: ID) {
    allIssueSubcategories(categoryId: $categoryId) {
      edges {
        node {
          id
          name
          defaultSeverity
          responsibleUser {
            id
            fullName
          }
          caseHandler {
            id
            fullName
          }
        }
      }
    }
  }
`)

export const CREATE_SUBCATEGORY = gql`
  mutation CreateIssueSubcategory($input: CreateIssueSubcategoryInput!) {
    createIssueSubcategory(input: $input) {
      issueSubcategory {
        id
        name
        defaultSeverity

        responsibleUser {
          id
          fullName
        }
        caseHandler {
          id
          fullName
        }
      }
    }
  }
`

export const ALL_ISSUE_DATA_FOR_SETTINGS_QUERY = gql`
  query AllIssueDataForSettings {
    allIssueCategories {
      edges {
        node {
          id
          name
          color
          editable
          issueSubcategories {
            edges {
              node {
                id
                name
                defaultSeverity
              }
            }
          }
          responsibleUser {
            id
            fullName
            firstName
            lastName
            initials
            profilePicture
          }
          caseHandler {
            id
            fullName
            firstName
            lastName
            initials
            profilePicture
          }
          defaultSeverity
          automaticReminderInterval
          minimumSeverityForReminder
          remindCreator
          remindResponsible
          defaultExportText
        }
      }
    }
    allIssueRiskAspects {
      edges {
        node {
          id
          name
        }
      }
    }
    allIssueRelations {
      edges {
        node {
          id
          name
          multipleChoice
          visibleOnCreate
          requiredOnCreate
          visibleOnEdit
          requiredOnEdit
          visibleInTable
          options {
            edges {
              node {
                id
                name
              }
            }
          }
        }
      }
    }
    allIssueProcesses {
      edges {
        node {
          id
          name
          choices {
            edges {
              node {
                id
                name
                order
              }
            }
          }
        }
      }
    }
  }
`

export const USER_ISSUES = gql`
  query UserIssues($user: ID, $dateGte: DateTime, $dateLt: DateTime) {
    allIssues(user: $user, createdAt_Gte: $dateGte, createdAt_Lt: $dateLt) {
      edges {
        node {
          id
          category {
            id
            name
          }
          user {
            ...UserInfo
          }
          timeOfIssue
          actualCost
          referenceId
          vehiclePlateNumber
          title
          damageDescription
          issueDescription
          locationPlacename
          locationLatitude
          locationLongitude
          locationDescription
          solutionSuggestion
          costEstimation
          closedBy {
            ...UserInfo
          }
          closedAt
          solution
        }
      }
    }
  }
`
